// Personal pane views (P08): layout (pane types/projects/filters/order) per user+workspace on the server,
// widths per device. Saved locally first (IndexedDB meta) so layout changes work offline; pushed when online.
// Personal data → the user's latest action wins (on revision conflict, re-base onto the server revision).
import type { Desks, Pane } from '@/domain/model';
import type { WorkspaceStore } from '@/db/workspaceStore';
import { api } from '@/api/client';

/** v10: every desktop's layout (server panes only, without widths); `cur` = the desktop whose panes are `layout`. */
type ServerDesks = { cur: string; list: Array<{ id: string; name: string; theme?: string; panes: Array<Omit<Pane, 'w'>> | null }> };
interface ServerView { layout: Array<Omit<Pane, 'w'>> | null; revision: number; desktops?: ServerDesks | null; devices?: Record<string, { widths: Record<string, number> }> }

export class ViewSync {
  private timer: ReturnType<typeof setTimeout> | null = null;
  constructor(private readonly store: WorkspaceStore, private readonly workspaceId: string, private readonly deviceId: string) {}

  private base() { return `/workspaces/${this.workspaceId}/me`; }
  /** Pane types the server layout accepts (src/Modules/Preferences PaneLayout::TYPES). Unknown future types are kept on
   *  this device only: sending them would reject the whole layout (422) and stall layout sync. */
  static readonly SERVER_TYPES = new Set(['projects', 'profile', 'changelog', 'trash', 'allstats', 'history', 'mentions', 'oldest', 'invites', 'md', 'notifs', 'updates']);
  static isServerPane(p: Pane): boolean { return !p.type || ViewSync.SERVER_TYPES.has(p.type); }
  static split(panes: Pane[]): { layout: Array<Omit<Pane, 'w'>>; widths: Record<string, number> } {
    const widths: Record<string, number> = {};
    const layout = panes.filter(ViewSync.isServerPane).map(({ w, ...rest }) => { if (w) widths[rest.id] = Math.round(w); return rest; });
    return { layout, widths };
  }
  static join(layout: Array<Omit<Pane, 'w'>>, widths: Record<string, number>): Pane[] { return layout.map(p => (widths[p.id] ? { ...p, w: widths[p.id] } : { ...p })); }

  /** Re-inserts this device's local-only panes into an adopted server layout, after the pane they followed. */
  static keepLocal(server: Pane[], local: Pane[]): Pane[] {
    const out = server.slice();
    local.forEach((p, i) => {
      if (ViewSync.isServerPane(p) || out.some(x => x.id === p.id)) return;
      let at = out.length;
      for (let j = i - 1; j >= 0; j--) { const k = out.findIndex(x => x.id === local[j]!.id); if (k >= 0) { at = k + 1; break; } }
      out.splice(at, 0, p);
    });
    return out;
  }

  /** v10: desktops for the server (server pane types only, widths are per device and stay local). */
  static serverDesks(d: Desks | null | undefined): ServerDesks | null {
    if (!d || !d.list.length) return null;
    return { cur: d.cur, list: d.list.map(x => ({ id: x.id, name: x.name, ...(x.theme ? { theme: x.theme } : {}), panes: x.panes ? ViewSync.split(x.panes).layout : null })) };
  }

  /** Local save (always) + debounced server push. */
  private last = '';
  /** Bumped by every local change: a push that started before a newer change must not mark the layout clean. */
  private gen = 0;
  /** What the server holds for this view (layout, this device's widths, desktops) as far as this tab knows. */
  private sent = '';
  static serverKey(panes: Pane[], desks: Desks | null | undefined): string { const { layout, widths } = ViewSync.split(panes); return JSON.stringify([layout, widths, ViewSync.serverDesks(desks)]); }
  save(panes: Pane[], desks?: Desks): void {
    const json = JSON.stringify([panes, desks || null]);
    if (json === this.last) return; // the view-model persists on every render-affecting change
    this.last = json;
    const [plain, plainDesks] = JSON.parse(json) as [Pane[], Desks | null];
    // Nothing the server keeps changed (e.g. the layout just adopted from another device): store it locally only.
    // Pushing it back made two devices of the same user hand the same layout to each other forever.
    if (ViewSync.serverKey(plain, plainDesks) === this.sent) { void this.store.setMeta('panes', plain).then(() => this.store.setMeta('desks', plainDesks)); return; }
    this.gen++;
    void this.store.setMeta('panes', plain).then(() => this.store.setMeta('desks', plainDesks)).then(() => this.store.setMeta('panes_dirty', true));
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.push(), 800);
  }

  async push(): Promise<void> {
    if (!(await this.store.getMeta<boolean>('panes_dirty'))) return;
    const gen = this.gen;
    const panes = (await this.store.getMeta<Pane[]>('panes')) || [];
    const { layout, widths } = ViewSync.split(panes);
    const desktops = ViewSync.serverDesks(await this.store.getMeta<Desks>('desks'));
    let rev = (await this.store.getMeta<number>('panes_rev')) ?? 0;
    try {
      for (let i = 0; i < 2; i++) {
        try {
          const r = await api<{ revision: number }>('PUT', this.base() + '/view', { layout, base_revision: rev, ...(desktops ? { desktops } : {}) });
          rev = r.revision; break;
        } catch (e) {
          const cur = (e as { status?: number }).status === 409 ? await api<ServerView>('GET', this.base() + '/view') : null;
          if (!cur) throw e;
          rev = cur.revision;
        }
      }
      await api('PUT', `${this.base()}/devices/${this.deviceId}/view`, { widths });
      await this.store.setMeta('panes_rev', rev);
      this.sent = ViewSync.serverKey(panes, await this.store.getMeta<Desks>('desks'));
      if (gen === this.gen) await this.store.setMeta('panes_dirty', false); // else a newer change is still to be pushed
    } catch { /* offline or transient: stays dirty, retried after the next sync */ }
  }

  /** On start/sync: push pending local changes, otherwise adopt a newer server layout. Returns panes to show or null. */
  async pull(): Promise<{ panes: Pane[]; desks: Desks | null } | null> {
    if (await this.store.getMeta<boolean>('panes_dirty')) { await this.push(); return null; }
    let v: ServerView;
    try { v = await api<ServerView>('GET', this.base() + '/view'); } catch { return null; }
    const localRev = (await this.store.getMeta<number>('panes_rev')) ?? 0;
    if (!v.layout || v.revision <= localRev) return null;
    const widths = v.devices?.[this.deviceId]?.widths || {};
    const panes = ViewSync.keepLocal(ViewSync.join(v.layout, widths), (await this.store.getMeta<Pane[]>('panes')) || []);
    // v10: desktops from the server; the current one shows `layout` (other desktops keep this device's widths too).
    const desks: Desks | null = v.desktops && v.desktops.list.length ? { cur: v.desktops.cur, list: v.desktops.list.map(x => ({ id: x.id, name: x.name, ...(x.theme ? { theme: x.theme } : {}), panes: x.id === v.desktops!.cur ? panes : x.panes ? ViewSync.join(x.panes, widths) : null })) } : null;
    await this.store.setMeta('panes', panes);
    if (desks) await this.store.setMeta('desks', desks);
    await this.store.setMeta('panes_rev', v.revision);
    this.last = JSON.stringify([panes, desks || (await this.store.getMeta<Desks>('desks')) || null]);
    this.sent = ViewSync.serverKey(panes, desks || (await this.store.getMeta<Desks>('desks')));
    return { panes, desks };
  }
}
